import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface SnackbarMessage {
  text: string;
  /** For example Undo. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds. Messages with an action stay long enough to use. */
  duration?: number;
}

const Context = createContext<(message: SnackbarMessage) => void>(() => undefined);

/** Shows the latest message in a polite live region. One at a time; a new one replaces the old. */
export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<SnackbarMessage | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback((next: SnackbarMessage) => {
    if (timer.current !== null) clearTimeout(timer.current);
    setMessage(next);
    timer.current = setTimeout(
      () => setMessage(null),
      next.duration ?? (next.action ? 5000 : 3500),
    );
  }, []);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  return (
    <Context.Provider value={show}>
      {children}
      <div className="k-snackbar-region" role="status" aria-live="polite">
        {message === null ? null : (
          <div className="k-snackbar k-t-body-lg">
            <span>{message.text}</span>
            {message.action === undefined ? null : (
              <button
                type="button"
                className="k-snackbar__action k-layer k-t-label-lg"
                onClick={() => {
                  message.action?.onClick();
                  setMessage(null);
                }}
              >
                {message.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </Context.Provider>
  );
}

export function useSnackbar(): (message: SnackbarMessage) => void {
  return useContext(Context);
}
